import React from "react";
import { Check } from "lucide-react";

const GROUPS = [
  {
    title: "Cost & access",
    rows: [
      { label: "Typical cost",
        diy: { t: "text", main: "£0", sub: "Your time only" },
        basic: { t: "text", main: "£59.99–£149.99", sub: "Template fee" },
        pro: { t: "text", main: "£150–£400+", sub: "Varies by complexity" } },
      { label: "Time to complete",
        diy: { t: "text", main: "Days to weeks" },
        basic: { t: "text", main: "1–2 hours" },
        pro: { t: "text", main: "1–2 appointments" } },
    ],
  },
  {
    title: "Legal standing",
    rows: [
      { label: "Legally valid",
        diy: { t: "pill", text: "High error risk", tone: "red" },
        basic: { t: "pill", text: "Some risk", tone: "amber" },
        pro: { t: "checktext", text: "Professionally checked" } },
      { label: "Expert review", diy: false, basic: false, pro: true },
      { label: "Handles complex estates", diy: false, basic: false, pro: true },
    ],
  },
  {
    title: "Personalisation",
    rows: [
      { label: "Tailored to your situation",
        diy: { t: "text", main: "Only if you know how" },
        basic: { t: "text", main: "Template limits apply" },
        pro: true },
      { label: "Blended families & trusts", diy: false, basic: false, pro: true },
      { label: "Guardianship for children", diy: false, basic: false, pro: true },
    ],
  },
  {
    title: "Protection",
    rows: [
      { label: "Reduces family disputes", diy: false, basic: false, pro: true },
      { label: "Inheritance tax planning", diy: false, basic: false, pro: true },
      { label: "Ongoing support & updates", diy: false, basic: false, pro: true },
    ],
  },
];

function Box({ checked, pro }) {
  if (checked) {
    return (
      <span className={`inline-flex items-center justify-center w-6 h-6 rounded-md ${pro ? "bg-white/15 border border-white/40" : "bg-brand-green/10 border border-brand-green/50"}`}>
        <Check className={`w-4 h-4 ${pro ? "text-white" : "text-brand-green"}`} strokeWidth={3} />
      </span>
    );
  }
  return <span className={`inline-block w-6 h-6 rounded-md border ${pro ? "border-white/25" : "border-brand-line"}`} />;
}

function Cell({ value, pro }) {
  if (value === true || value === false) return <Box checked={value} pro={pro} />;
  if (value?.t === "text") {
    return (
      <div className="leading-tight">
        <div className={`font-serif ${pro ? "text-white text-lg" : "text-brand-ink text-base"}`}>{value.main}</div>
        {value.sub && <div className={`text-xs mt-0.5 ${pro ? "text-white/70" : "text-brand-muted"}`}>{value.sub}</div>}
      </div>
    );
  }
  if (value?.t === "pill") {
    const tones = { red: "bg-red-50 text-red-600", amber: "bg-amber-50 text-amber-700" };
    return (
      <div className="flex flex-col items-center gap-1.5">
        <Box checked={false} pro={pro} />
        <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full ${tones[value.tone] || ""}`}>{value.text}</span>
      </div>
    );
  }
  if (value?.t === "checktext") {
    return (
      <div className="flex flex-col items-center gap-1.5">
        <Box checked pro={pro} />
        <span className="text-[11px] font-semibold text-white/85">{value.text}</span>
      </div>
    );
  }
  return null;
}

export function WillComparison() {
  return (
    <div className="rounded-2xl border border-brand-line bg-brand-bg p-6 md:p-9" data-testid="will-comparison">
      <div className="flex items-center gap-2 text-brand-gold text-xs font-semibold tracking-[0.15em] uppercase mb-3">
        <span className="inline-block w-3.5 h-3.5 rounded-full border-2 border-brand-gold" />
        Estate planning · Rehoboth Wealth Group
      </div>
      <h2 className="font-serif text-3xl md:text-4xl text-brand-green">Which will is right for you?</h2>
      <p className="text-brand-muted mt-2 mb-7">Understanding your options before making one of life's most important decisions.</p>

      <div className="overflow-x-auto">
        <div className="grid grid-cols-[1.7fr_1fr_1fr_1.2fr] min-w-[660px] rounded-xl overflow-hidden border border-brand-line">
          {/* Header */}
          <div className="bg-[#EDEAE0] px-4 py-5 border-b border-brand-line" />
          <div className="bg-[#EDEAE0] px-4 py-5 border-b border-brand-line text-center">
            <div className="font-serif text-xl text-brand-ink">DIY will</div>
            <div className="text-xs font-semibold text-brand-muted mt-0.5">Write it yourself</div>
          </div>
          <div className="bg-[#EDEAE0] px-4 py-5 border-b border-brand-line text-center">
            <div className="font-serif text-xl text-brand-ink">Basic will</div>
            <div className="text-xs font-semibold text-brand-muted mt-0.5">Online template</div>
          </div>
          <div className="bg-brand-green px-4 py-4 text-center">
            <span className="inline-block text-[10px] font-bold tracking-wider text-brand-gold border border-brand-gold rounded-full px-2.5 py-0.5 mb-1.5">RECOMMENDED</span>
            <div className="font-serif text-xl text-white">Professional will</div>
            <div className="text-xs font-semibold text-white/70 mt-0.5">Written by a will writer</div>
          </div>

          {/* Groups */}
          {GROUPS.map((g) => (
            <React.Fragment key={g.title}>
              <div className="col-span-3 bg-[#E4E0D4] px-4 py-2 border-b border-brand-line text-xs font-bold tracking-wider uppercase text-brand-muted">{g.title}</div>
              <div className="bg-brand-green border-b border-white/10" />
              {g.rows.map((r, ri) => {
                const lastGroup = g === GROUPS[GROUPS.length - 1];
                const lastRow = lastGroup && ri === g.rows.length - 1;
                return (
                  <React.Fragment key={r.label}>
                    <div className={`bg-white px-4 py-4 flex items-center text-sm text-brand-ink ${lastRow ? "" : "border-b border-brand-line"}`}>{r.label}</div>
                    <div className={`bg-white flex items-center justify-center text-center px-4 py-4 ${lastRow ? "" : "border-b border-brand-line"}`}><Cell value={r.diy} /></div>
                    <div className={`bg-white flex items-center justify-center text-center px-4 py-4 ${lastRow ? "" : "border-b border-brand-line"}`}><Cell value={r.basic} /></div>
                    <div className={`bg-brand-green flex items-center justify-center text-center px-4 py-4 ${lastRow ? "rounded-b-xl" : "border-b border-white/10"}`}><Cell value={r.pro} pro /></div>
                  </React.Fragment>
                );
              })}
            </React.Fragment>
          ))}
        </div>
      </div>
    </div>
  );
}
